import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePendingAssetInsertions, type PendingAssetInsertion } from '@/state/pendingAssetInsertions.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { copyAssetsForInsert } from '../copyAssetsForInsert';
import { useFlowView } from '../FlowContext';
import type { AssetReferenceSource } from '@/api/assetCopy.api';

export function usePendingAssetInsertion() {
  const flowView = useFlowView();
  const workflowId = useWorkflowStore((state) => state.recipe.id);
  const pendingIds = useRef(new Set<string>());

  useEffect(() => {
    const ids = pendingIds.current;
    return () => {
      for (const id of ids) usePendingAssetInsertions.getState().remove(id);
      ids.clear();
    };
  }, [flowView, workflowId]);

  return useCallback(
    <T>(
      source: AssetReferenceSource,
      payload: T,
      preview: Omit<PendingAssetInsertion, 'workflowId'>,
      insert: (copied: T) => void,
      onError: (error: unknown) => void,
    ) => {
      const id = uuidv4();
      pendingIds.current.add(id);
      usePendingAssetInsertions.getState().add(id, { workflowId, ...preview });
      const isCurrent = () =>
        !!usePendingAssetInsertions.getState().insertions[id] && useWorkflowStore.getState().recipe.id === workflowId;

      void copyAssetsForInsert(source, workflowId, payload, isCurrent)
        .then((copied) => {
          if (isCurrent()) insert(copied);
        })
        .catch((error) => {
          if (isCurrent()) onError(error);
        })
        .finally(() => {
          pendingIds.current.delete(id);
          usePendingAssetInsertions.getState().remove(id);
        });
    },
    [workflowId],
  );
}
